import { AgGridModule } from '@ag-grid-community/angular';
import { ColDef } from '@ag-grid-community/core/dist/esm/es6/entities/colDef';
import { ModuleRegistry } from '@ag-grid-community/core/dist/esm/es6/modules/moduleRegistry';
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model/dist/esm/es6/serverSideRowModelModule';
import { SetFilterModule } from '@ag-grid-enterprise/set-filter/dist/esm/es6/setFilterModule';
import { StatusBarModule } from '@ag-grid-enterprise/status-bar/dist/esm/es6/statusBarModule';

import { Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, switchMap } from 'rxjs';
import { BaseGrid } from 'shared/abstract/base-grid';
import { DateFloatingFilterComponent } from 'shared/components/ag-grid/filters/date-floating-filter/date-floating-filter.component';
import { CurrentSupplierUserInterface } from 'shared/interfaces/current-supplier-user.interface';
import { AccessTypeEnum, PermissionAccessZoneEnum } from 'shared/interfaces/permissions.interface';
import { EventBusService } from 'shared/services/event-bus.service';
import { ModalService } from 'shared/services/modal.service';
import { ToastService } from 'shared/services/toast.service';
import { UserApiService } from 'shared/services/userApi.service';
import { supplierUserHasAccess } from 'shared/util/supplierUserHasAccess';
import { User, UserGroup } from 'users/interfaces/user.interface';
import { UserGroupsService } from 'users/services/user-groups.service';
import { CreateEditUserGroupsComponent } from '../create-edit-user-groups/create-edit-user-groups.component';

@Component({
    selector: 'app-user-groups-table',
    templateUrl: './user-groups-table.component.html',
    styleUrls: ['./user-groups-table.component.scss'],
    imports: [AgGridModule, TranslateModule],
})
export class UserGroupsTableComponent extends BaseGrid implements OnInit, OnDestroy {
    private isAdminUser: boolean = false;

    //TODO column headers should be translated as well
    public columnDefs: ColDef[] = [
        {
            field: 'companyName',
            unSortIcon: true,
            floatingFilter: true,
            hide: true,
            floatingFilterComponentParams: { suppressFilterButton: true },
            width: 180,
            comparator: (valueA, valueB) => {
                return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
            },
        },
        {
            field: 'name',
            headerName: 'Group Name',
            unSortIcon: true,
            floatingFilter: true,
            floatingFilterComponentParams: { suppressFilterButton: true },
            width: 180,
            comparator: (valueA, valueB) => {
                return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
            },
        },
        { field: 'entityTracking.createdBy', headerName: 'Created By', sortable: false, width: 150 },
        {
            field: 'entityTracking.createdAt',
            width: 150,
            colId: 'createdAt',
            headerName: 'Created At',
            sortable: false,
            floatingFilter: true,
            floatingFilterComponent: DateFloatingFilterComponent,
            floatingFilterComponentParams: { suppressFilterButton: true },
            valueFormatter: this.dateFormatter,
        },
        { field: 'entityTracking.modifiedBy', headerName: 'Modified By', sortable: false, width: 150 },
        {
            field: 'entityTracking.modifiedAt',
            width: 150,
            colId: 'modifiedAt',
            headerName: 'Modified At',
            sortable: false,
            floatingFilter: true,
            floatingFilterComponent: DateFloatingFilterComponent,
            floatingFilterComponentParams: { suppressFilterButton: true },
            valueFormatter: this.dateFormatter,
        },
        {
            colId: 'actions',
            filter: false,
            resizable: false,
            sortable: false,
            minWidth: 110,
            maxWidth: 110,
            headerComponent: 'resetFiltersHeader',
            cellRenderer: 'actionButtonsCellRenderer',
            cellRendererParams: {
                buttons: [
                    {
                        clickAction: this.deleteUserGroupButtonClickHandler.bind(this),
                        icon: 'trash',
                        computeDisabledState: this.isDeleteDisabledForRow.bind(this),
                        computeTooltip: this.computeDeleteTooltip.bind(this),
                    },
                    {
                        clickAction: this.editUserGroupButtonClickHandler.bind(this),
                        icon: 'pen-to-square',
                        computeDisabledState: this.isEditDisabledForRow.bind(this),
                        computeTooltip: this.computeEditTooltip.bind(this),
                    },
                ],
            },
        },
    ];

    constructor(
        router: Router,
        activatedRoute: ActivatedRoute,
        destroyRef: DestroyRef,
        private userGroupsGridService: UserGroupsService,
        private toastService: ToastService,
        private translateService: TranslateService,
        private modalService: ModalService,
        private eventBusService: EventBusService,
        public userApiService: UserApiService,
    ) {
        ModuleRegistry.registerModules([ServerSideRowModelModule, StatusBarModule, SetFilterModule]);
        super(userGroupsGridService, router, activatedRoute, destroyRef);
    }

    ngOnInit(): void {
        //TODO: extract this in a general method
        this.subscriptions.getLoginInfo = this.userApiService.getLoginInformation().subscribe((userInformation) => {
            this.isAdminUser = userInformation.isShiji;
            if (userInformation.isShiji) {
                this.columnDefs[0].hide = false;
                this.agGrid?.api?.setColumnVisible('companyName', true);
            } else {
                //supplier user should have at least "Edit" permissions
                userInformation = userInformation as CurrentSupplierUserInterface;

                const actionsColumn = this.columnDefs.find((x) => x.colId == 'actions') as ColDef;
                const shouldShowActions = supplierUserHasAccess(
                    userInformation.internalPermissions,
                    PermissionAccessZoneEnum.ACCESS_TO_USER_GROUPS,
                    AccessTypeEnum.EDIT,
                );

                if (!shouldShowActions) {
                    actionsColumn.cellRenderer = null;
                    actionsColumn.maxWidth = 60;
                }
            }
        });

        this.subscriptions.pageMainAction = this.eventBusService.getObservableForEventStream('pageMainButtonAction').subscribe((data) => {
            if (data === 'userGroups') {
                this.openCreateUserGroupModal();
            }
        });
    }

    public deleteUserGroupButtonClickHandler(rowData: UserGroup) {
        this.subscriptions.deleteUserGroupButton = this.modalService
            .openDeleteConfirmationModal(
                this.translateService.instant('users.userGroups.deleteModal.body'),
                this.translateService.instant('users.userGroups.deleteModal.title'),
                rowData.companyName,
                rowData.name,
                this.translateService.instant('users.userGroups.deleteModal.entityTypeName'),
            )
            .onClose.pipe(
                filter(Boolean),
                switchMap(() => {
                    return this.userGroupsGridService.deleteUserGroupById(rowData.id);
                }),
            )
            .subscribe(() => {
                this.toastService.showToast('users.userGroups.deleteSuccess', 'success', { groupName: rowData.name });
                this.agGrid?.api?.refreshServerSide({ purge: true });
            });
    }

    public editUserGroupButtonClickHandler(rowData: User) {
        const modalData = { ...rowData, type: 'side' };
        const reference = this.modalService.openModal(CreateEditUserGroupsComponent, modalData);

        this.subscriptions.editUserGroupButton = reference.onClose.subscribe((isSaved) => {
            if (isSaved) {
                this.agGrid?.api?.refreshServerSide({ purge: true });
            }
        });
    }

    private computeDeleteTooltip(rowData: any) {
        if (rowData.isAdmin === true) {
            return 'users.userGroups.cannotDeleteAdminGroupTooltip';
        }

        return this.isDeleteDisabledForRow(rowData) ? 'users.userGroups.deleteUserGroupWithUsersTooltip' : 'generalTooltips.delete';
    }

    private computeEditTooltip(rowData: any) {
        return this.isEditDisabledForRow(rowData) ? 'users.userGroups.cannotEditAdminGroupTooltip' : 'generalTooltips.edit';
    }

    private isDeleteDisabledForRow(rowData: any): boolean {
        return rowData.isAdmin === true || rowData.userAccesses?.length > 0;
    }

    private isEditDisabledForRow(rowData: any): boolean {
        return !this.isAdminUser && rowData.isAdmin === true;
    }

    private openCreateUserGroupModal() {
        this.modalService
            .openModal(CreateEditUserGroupsComponent, { type: 'side' })
            .onClose.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((isSaved) => {
                if (isSaved) {
                    if (this.agGrid) {
                        this.agGrid?.api?.refreshServerSide({ purge: true });
                    } else {
                        this.anyRows.set(true);
                    }
                }
            });
    }
}
